import {
  AmazonServices,
  ApcServices,
  AsendiaServices,
  AustraliaPostServices,
  CanadaPostServices,
  DhlEcsDistributionCenter,
  DhlEcsServices,
  DhlExpressServices,
  EPostGlobalServices,
  FirstMileServices,
  GlsServices,
  LandmarkGlobalServices,
  OnTracServices,
  PassportGlobalServices,
  RoyalMailV3Services,
  SpeeDeeServices,
  UpsServices,
  VehoServices,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import { LengthUnit } from "@redotech/redo-fulfillment-outbound-labels-common/util";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RateProvider } from "../outbound-labels/outbound-labels";
import {
  FedexRateTableServiceLevel,
  USPSRateTableServiceLevel,
  USPSReturnsRateTableServiceLevel,
} from "./rate-table-mapping";

export enum AxisKind {
  ZONE = "zone",
  WEIGHT = "weight",
  CUBIC_SIZE = "cubic_size",
  PACKAGE = "package",
}

export enum WeightUnit {
  OZ = "OZ",
  LB = "LB",
}

export enum RateTableType {
  OUTBOUND_WEIGHT_BY_ZONE = "outbound_weight_by_zone",
  OUTBOUND_SIZE_BY_ZONE = "outbound_size_by_zone",
  RETURNS_WEIGHT_BY_ZONE = "returns_weight_by_zone",
  RETURNS_DEFAULT_WEIGHT_BY_ZONE = "returns_default_weight_by_zone",
  RETURNS_DEFAULT_BY_WEIGHT = "returns_default_by_weight",
  RETURNS_TEAM_WEIGHT_BY_ZONE = "returns_team_weight_by_zone",
  RETURNS_TEAM_BY_WEIGHT = "returns_team_by_weight",
  OUTBOUND_PACKAGE_BY_ZONE = "outbound_package_by_zone",
}

export type OutboundRateTableType =
  | RateTableType.OUTBOUND_WEIGHT_BY_ZONE
  | RateTableType.OUTBOUND_SIZE_BY_ZONE;

// Are the values meant to be used as the values we charge the customer (rate) or are they the margins that we add to the base rate (spread)
export enum RateTableValueMode {
  /** The table represents the values we charge */
  Rate = "Rate",
  /** The table represents the margins that we add to the base rate */
  Spread = "Spread",
  /** The table represents the percentage markup applied to the base rate */
  SpreadPercent = "SpreadPercent",
  /** The table represents the percentage discount off of the base rate */
  Discount = "Discount",
}

const zoneAxisSchema = z.object({
  kind: z.literal(AxisKind.ZONE),
  values: z.array(z.string()),
});

const weightAxisSchema = z.object({
  kind: z.literal(AxisKind.WEIGHT),
  units: createEnum(WeightUnit, { enumName: "rate_table_weight_unit" }),
  values: z.array(z.object({ minWeight: z.number(), maxWeight: z.number() })),
});

const cubicSizeAxisSchema = z.object({
  kind: z.literal(AxisKind.CUBIC_SIZE),
  units: createEnum(LengthUnit, { enumName: "rate_table_length_unit" }),
  values: z.array(
    z.object({ minCubicSize: z.number(), maxCubicSize: z.number() }),
  ),
});

const packageAxisSchema = z.object({
  kind: z.literal(AxisKind.PACKAGE),
  values: z.array(z.string()),
});

// Service level enum params - all carrier enums share the same enumName
// so the zod-extractor merges all values into a single enum for DDL generation
const serviceLevelEnumParams = {
  enumName: "rate_table_service_level",
} as const;

// Flat union of all carrier service enums - the extractor merges them by enumName
export const serviceLevelsSchema = z.union([
  createEnum(USPSRateTableServiceLevel, serviceLevelEnumParams),
  createEnum(USPSReturnsRateTableServiceLevel, serviceLevelEnumParams),
  createEnum(FedexRateTableServiceLevel, serviceLevelEnumParams),
  createEnum(UpsServices, serviceLevelEnumParams),
  createEnum(AmazonServices, serviceLevelEnumParams),
  createEnum(DhlExpressServices, serviceLevelEnumParams),
  createEnum(VehoServices, serviceLevelEnumParams),
  createEnum(FirstMileServices, serviceLevelEnumParams),
  createEnum(AustraliaPostServices, serviceLevelEnumParams),
  createEnum(CanadaPostServices, serviceLevelEnumParams),
  createEnum(DhlEcsServices, serviceLevelEnumParams),
  createEnum(ApcServices, serviceLevelEnumParams),
  createEnum(GlsServices, serviceLevelEnumParams),
  createEnum(AsendiaServices, serviceLevelEnumParams),
  createEnum(RoyalMailV3Services, serviceLevelEnumParams),
  createEnum(SpeeDeeServices, serviceLevelEnumParams),
  createEnum(PassportGlobalServices, serviceLevelEnumParams),
  createEnum(LandmarkGlobalServices, serviceLevelEnumParams),
  createEnum(OnTracServices, serviceLevelEnumParams),
  createEnum(EPostGlobalServices, serviceLevelEnumParams),
  createEnum(["Default"] as const, serviceLevelEnumParams),
]);

const _baseSchema = z.object({
  version: z.number().nullish(),
  rateTableType: createEnum(RateTableType, {
    enumName: "rate_table_schema_type",
  }),
  valueMode: createEnum(RateTableValueMode, {
    enumName: "rate_table_value_mode",
  }),
  data: z.array(z.array(z.string())),
  defaultValue: z.string(),
  defaultValuePercentage: z.number().min(0).max(100).nullish(),
  useDefaultValuePercentage: z.boolean().nullish(),
});

const _baseOmsSchema = _baseSchema.extend({
  provider: createEnum(RateProvider, { enumName: "RateProvider" }),
  carrier: createEnum(RateTableCarrier, { enumName: "rate_table_carrier" }),
  serviceLevel: serviceLevelsSchema,
  distributionCenterId: createEnum(DhlEcsDistributionCenter, {
    enumName: "dhl_ecs_distribution_center",
  }).nullish(),
});

export const outboundWeightByZoneSchema = _baseOmsSchema.extend({
  rateTableType: z.literal(RateTableType.OUTBOUND_WEIGHT_BY_ZONE),
  xAxis: zoneAxisSchema,
  yAxis: weightAxisSchema,
});

const outboundCubicSizeByZoneSchema = _baseOmsSchema.extend({
  rateTableType: z.literal(RateTableType.OUTBOUND_SIZE_BY_ZONE),
  xAxis: zoneAxisSchema,
  yAxis: cubicSizeAxisSchema,
});

const outboundPackageByZoneSchema = _baseOmsSchema.extend({
  rateTableType: z.literal(RateTableType.OUTBOUND_PACKAGE_BY_ZONE),
  xAxis: zoneAxisSchema,
  yAxis: packageAxisSchema,
});

export const returnsWeightByZoneSchema = _baseSchema.extend({
  rateTableType: z.literal(RateTableType.RETURNS_WEIGHT_BY_ZONE),
  provider: createEnum(RateProvider, { enumName: "RateProvider" }),
  xAxis: zoneAxisSchema,
  yAxis: weightAxisSchema,
});

export const returnsDefaultWeightByZoneSchema = _baseSchema.extend({
  rateTableType: z.literal(RateTableType.RETURNS_DEFAULT_WEIGHT_BY_ZONE),
  xAxis: zoneAxisSchema,
  yAxis: weightAxisSchema,
  carrier: createEnum(RateTableCarrier, { enumName: "rate_table_carrier" }),
  serviceLevel: serviceLevelsSchema,
});

export const returnsDefaultDiscountByWeightSchema = _baseSchema.extend({
  rateTableType: z.literal(RateTableType.RETURNS_DEFAULT_BY_WEIGHT),
  // yAxis-only rate table: discount is uniform across weights via
  // defaultValue, so xAxis is unused. Optional rather than .default(...) so
  // the CongoDB schema-extractor emits a nullable x_axis_kind column —
  // .default() keeps NOT NULL semantics, which broke the backfill on docs
  // that pre-date the default. See INFRA-1157.
  xAxis: zoneAxisSchema.optional(),
  yAxis: weightAxisSchema,
  carrier: createEnum(RateTableCarrier, { enumName: "rate_table_carrier" }),
  serviceLevel: serviceLevelsSchema,
});

export const returnsTeamWeightByZoneSchema = _baseSchema.extend({
  rateTableType: z.literal(RateTableType.RETURNS_TEAM_WEIGHT_BY_ZONE),
  xAxis: zoneAxisSchema,
  yAxis: weightAxisSchema,
});

export const returnsTeamDiscountByWeightSchema = _baseSchema.extend({
  rateTableType: z.literal(RateTableType.RETURNS_TEAM_BY_WEIGHT),
  // yAxis-only rate table: see returnsDefaultDiscountByWeightSchema above.
  xAxis: zoneAxisSchema.optional(),
  yAxis: weightAxisSchema,
});

export const rateTableSchema = z
  .discriminatedUnion("rateTableType", [
    outboundWeightByZoneSchema,
    outboundCubicSizeByZoneSchema,
    outboundPackageByZoneSchema,
    returnsWeightByZoneSchema,
    returnsDefaultWeightByZoneSchema,
    returnsDefaultDiscountByWeightSchema,
    returnsTeamWeightByZoneSchema,
    returnsTeamDiscountByWeightSchema,
  ])
  .refine(
    ({ data, yAxis }) => data.length === yAxis.values.length,
    "Invalid number of rows",
  )
  .refine(
    ({ data, xAxis }) =>
      data.every((row) => row.length === (xAxis?.values.length ?? 0)),
    "Invalid number of columns",
  );

export const RateTableDocumentSchema = rateTableSchema.and(
  z.object({
    team: zExt.objectId("teams").optional(),
    createdAt: z.date().optional(),
    updatedAt: z.date().optional(),
  }),
);

export type OutboundCubicSizeByZoneRateTable = z.infer<
  typeof outboundCubicSizeByZoneSchema
>;

export type OutboundWeightByZoneRateTable = z.infer<
  typeof outboundWeightByZoneSchema
>;

export type OutboundPackageByZoneRateTable = z.infer<
  typeof outboundPackageByZoneSchema
>;

export type OutboundRateTable =
  | OutboundCubicSizeByZoneRateTable
  | OutboundWeightByZoneRateTable
  | OutboundPackageByZoneRateTable;

export type ReturnsWeightByZoneRateTable = z.infer<
  typeof returnsWeightByZoneSchema
>;

export type ReturnsDefaultWeightByZoneRateTable = z.infer<
  typeof returnsDefaultWeightByZoneSchema
>;

export type ReturnsDefaultDiscountByWeightTable = z.infer<
  typeof returnsDefaultDiscountByWeightSchema
>;

export type ReturnsTeamWeightByZoneRateTable = z.infer<
  typeof returnsTeamWeightByZoneSchema
>;

export type ReturnsTeamDiscountByWeightTable = z.infer<
  typeof returnsTeamDiscountByWeightSchema
>;

export type WeightByZoneRateTable =
  | OutboundWeightByZoneRateTable
  | ReturnsWeightByZoneRateTable;

export function isOutboundWeightByZoneRateTable(
  table: RateTable,
): table is OutboundWeightByZoneRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_WEIGHT_BY_ZONE;
}

export function isOutboundCubicSizeByZoneRateTable(
  table: RateTable,
): table is OutboundCubicSizeByZoneRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_SIZE_BY_ZONE;
}

function isOutboundPackageByZoneRateTable(
  table: RateTable,
): table is OutboundPackageByZoneRateTable {
  return table.rateTableType === RateTableType.OUTBOUND_PACKAGE_BY_ZONE;
}

export function isOutboundRateTable(
  table: RateTable,
): table is OutboundRateTable {
  return (
    isOutboundWeightByZoneRateTable(table) ||
    isOutboundCubicSizeByZoneRateTable(table) ||
    isOutboundPackageByZoneRateTable(table)
  );
}

export function isReturnsWeightByZoneRateTable(
  table: RateTable,
): table is ReturnsWeightByZoneRateTable {
  return table.rateTableType === RateTableType.RETURNS_WEIGHT_BY_ZONE;
}

export function isReturnsDefaultWeightByZoneRateTable(
  table: RateTable,
): table is ReturnsDefaultWeightByZoneRateTable {
  return table.rateTableType === RateTableType.RETURNS_DEFAULT_WEIGHT_BY_ZONE;
}

export type WeightAxis = z.infer<typeof weightAxisSchema>;
export type ZoneAxis = z.infer<typeof zoneAxisSchema>;
export type CubicSizeAxis = z.infer<typeof cubicSizeAxisSchema>;
export type PackageAxis = z.infer<typeof packageAxisSchema>;

const axisSchema = z.union([
  zoneAxisSchema,
  weightAxisSchema,
  cubicSizeAxisSchema,
  packageAxisSchema,
]);

export type AxisDef = z.infer<typeof axisSchema>;
export type RateTable = z.infer<typeof rateTableSchema>;
export type RateTableDocument = z.infer<typeof RateTableDocumentSchema>;

export interface ShipmentData {
  [AxisKind.ZONE]: string;
  [AxisKind.WEIGHT]: { unit: WeightUnit; value: number };
  [AxisKind.CUBIC_SIZE]?: { cubic_feet: number };
  [AxisKind.PACKAGE]?: string | null;
}

// A rate table is defined by the carrier, service level, rate table type (weight by zone or size by zone) and value mode (rate vs spread)
export function rateTableDefinitionEqual(
  a: OutboundRateTable,
  b: OutboundRateTable,
) {
  return (
    a.carrier === b.carrier &&
    a.serviceLevel === b.serviceLevel &&
    a.rateTableType === b.rateTableType &&
    a.valueMode === b.valueMode &&
    a.provider === b.provider
  );
}

// Ex: Easypost USPS Ground Advantage Spread
export function stringifiedDefinition(a: OutboundRateTable) {
  const rateTableTypeToLabel = {
    [RateTableType.OUTBOUND_WEIGHT_BY_ZONE]: "Weight",
    [RateTableType.OUTBOUND_SIZE_BY_ZONE]: "Cubic",
    [RateTableType.OUTBOUND_PACKAGE_BY_ZONE]: "Package",
  };
  const providerToLabel = {
    [RateProvider.EASYPOST]: "Easypost",
    [RateProvider.SOAP_BOX]: "Soapbox",
    [RateProvider.UPS]: "UPS",
    [RateProvider.LANDMARK_GLOBAL]: "Landmark Global",
    [RateProvider.REDO_LANDMARK_GLOBAL]: "Redo Landmark Global",
    [RateProvider.INTERNATIONAL_BRIDGE]: "International Bridge",
    [RateProvider.ETA_EXPRESS]: "ETA Express",
    [RateProvider.TEST_CARRIER]: "Test Carrier",
    [RateProvider.FEDEX]: "FedEx",
    [RateProvider.USPS]: "USPS",
    [RateProvider.DHL_ECOMMERCE]: "DHL eCommerce",
    [RateProvider.CANADA_POST]: "Canada Post",
    [RateProvider.GLOBALE]: "Global-E",
    [RateProvider.TIKTOK]: "TikTok",
    [RateProvider.UNIUNI]: "UniUni",
    [RateProvider.SENDCLOUD]: "Sendcloud",
    [RateProvider.STALLION_EXPRESS]: "Stallion Express",
    [RateProvider.CHITCHATS]: "Chit Chats",
  };
  return `${providerToLabel[a.provider]} ${a.carrier} ${a.serviceLevel} ${rateTableTypeToLabel[a.rateTableType]} ${a.valueMode}`;
}

export const defaultNewReturnsRateTable: ReturnsWeightByZoneRateTable = {
  rateTableType: RateTableType.RETURNS_WEIGHT_BY_ZONE,
  valueMode: RateTableValueMode.Spread,
  provider: RateProvider.EASYPOST,
  data: [
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485", "1.485"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98", "1.98"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475", "2.475"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
    ["2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97", "2.97"],
  ],
  xAxis: {
    values: ["1", "2", "3", "4", "5", "6", "7", "8"],
    kind: AxisKind.ZONE,
  },
  yAxis: {
    values: [
      { minWeight: 0, maxWeight: 0.0625 },
      { minWeight: 0.0625, maxWeight: 0.125 },
      { minWeight: 0.125, maxWeight: 0.1875 },
      { minWeight: 0.1875, maxWeight: 0.25 },
      { minWeight: 0.25, maxWeight: 0.3125 },
      { minWeight: 0.3125, maxWeight: 0.375 },
      { minWeight: 0.375, maxWeight: 0.4375 },
      { minWeight: 0.4375, maxWeight: 0.5 },
      { minWeight: 0.5, maxWeight: 0.5625 },
      { minWeight: 0.5625, maxWeight: 0.625 },
      { minWeight: 0.625, maxWeight: 0.6875 },
      { minWeight: 0.6875, maxWeight: 0.75 },
      { minWeight: 0.75, maxWeight: 0.8125 },
      { minWeight: 0.8125, maxWeight: 0.875 },
      { minWeight: 0.875, maxWeight: 0.9375 },
      { minWeight: 0.9375, maxWeight: 1 },
      { minWeight: 1, maxWeight: 2 },
      { minWeight: 2, maxWeight: 3 },
      { minWeight: 3, maxWeight: 4 },
      { minWeight: 4, maxWeight: 5 },
      { minWeight: 5, maxWeight: 6 },
      { minWeight: 6, maxWeight: 7 },
      { minWeight: 7, maxWeight: 8 },
      { minWeight: 8, maxWeight: 9 },
      { minWeight: 9, maxWeight: 10 },
      { minWeight: 10, maxWeight: 11 },
      { minWeight: 11, maxWeight: 12 },
      { minWeight: 12, maxWeight: 13 },
      { minWeight: 13, maxWeight: 14 },
      { minWeight: 14, maxWeight: 15 },
      { minWeight: 15, maxWeight: 16 },
      { minWeight: 16, maxWeight: 17 },
      { minWeight: 17, maxWeight: 18 },
      { minWeight: 18, maxWeight: 19 },
      { minWeight: 19, maxWeight: 20 },
      { minWeight: 20, maxWeight: 21 },
      { minWeight: 21, maxWeight: 22 },
      { minWeight: 22, maxWeight: 23 },
      { minWeight: 23, maxWeight: 24 },
      { minWeight: 24, maxWeight: 25 },
      { minWeight: 25, maxWeight: 26 },
      { minWeight: 26, maxWeight: 27 },
      { minWeight: 27, maxWeight: 28 },
      { minWeight: 28, maxWeight: 29 },
      { minWeight: 29, maxWeight: 30 },
      { minWeight: 30, maxWeight: 31 },
      { minWeight: 31, maxWeight: 32 },
      { minWeight: 32, maxWeight: 33 },
      { minWeight: 33, maxWeight: 34 },
      { minWeight: 34, maxWeight: 35 },
      { minWeight: 35, maxWeight: 36 },
      { minWeight: 36, maxWeight: 37 },
      { minWeight: 37, maxWeight: 38 },
      { minWeight: 38, maxWeight: 39 },
      { minWeight: 39, maxWeight: 40 },
      { minWeight: 40, maxWeight: 41 },
      { minWeight: 41, maxWeight: 42 },
      { minWeight: 42, maxWeight: 43 },
      { minWeight: 43, maxWeight: 44 },
      { minWeight: 44, maxWeight: 45 },
      { minWeight: 45, maxWeight: 46 },
      { minWeight: 46, maxWeight: 47 },
      { minWeight: 47, maxWeight: 48 },
      { minWeight: 48, maxWeight: 49 },
      { minWeight: 49, maxWeight: 50 },
    ],
    kind: AxisKind.WEIGHT,
    units: WeightUnit.LB,
  },
  defaultValue: "1",
};
